import React, { useContext } from 'react';
import MainLayout from '../../_layout/MainLayout';
import useSavedUsers from './useSavedUsers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Button, Modal, Spin } from 'antd';
import InputField from '../../_component/InputField';
import { AuthContext } from '../../_hooks/useAuthContext';
import { platformPermissions } from '../../_helpers/types';
import PlatformRolesDropdown from '../../_component/PlatformRolesDropdown';

function SavedUsers(props) {
  const { checkPermission } = useContext(AuthContext);
  const {
    user,
    loading,
    allUsers,
    userForm,
    userModalOpen,
    deleteUser,
    deleteModalOpen,
    formErrors,
    organisationDetails,
    organisationModalOpen,
    onInputChange,
    onUserSave,
    onUserDelete,
    handleUserModalOpen,
    onDeleteUserConfirm,
    handleOrganisationModalOpen,
    onOrganisationSave,
    onOrganisationInputChange,
  } = useSavedUsers(props);

  return (
    <MainLayout activeMenuItem="Contacts">
      <div className="p-4 sticky">
        <div className="centered justify-between mb-4 window-container">
          <h2 className="window-title">Users Management</h2>
          <div className="centered justify-center">
            {checkPermission(platformPermissions.createOrganisation) && !organisationDetails?._id && (
              <button
                className="btn bg-slate-50 text-gray-900 rounded-xl outline outline-1 hover:bg-slate-200 mr-3"
                onClick={() => handleOrganisationModalOpen()}
              >
                Add Organization
              </button>
            )}

            {checkPermission(platformPermissions.createUser) && organisationDetails?._id && (
              <button
                className="btn bg-slate-50 text-gray-900 rounded-xl outline outline-1 hover:bg-slate-200"
                onClick={() => handleUserModalOpen({})}
              >
                Add User
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="centered spinner-box">
            <Spin size="large" />
          </div>
        ) : (
          <div className="centered min-h-[90vh] flex-col gap-2 w-full">
            <div className="overflow-x-auto w-full flex-col mb-8">
              {organisationDetails?._id && (
                <div className="p-3 border border-gray-200 rounded-md bg-gray-100">
                  <h2 className="text-md font-medium text-gray-700">Organization Details</h2>
                  <p className="text-gray-600 py-2">
                    Name: <strong>{organisationDetails.name}</strong>{' '}
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="ml-2 text-blue-500"
                      onClick={() => handleOrganisationModalOpen(organisationDetails)}
                    />
                  </p>
                  <p className="text-gray-600">
                    URL:{' '}
                    <a href={organisationDetails.url} className="text-blue-500 hover:underline">
                      {organisationDetails.url}
                    </a>
                  </p>
                </div>
              )}
              {!organisationDetails?._id ? (
                <div className="mb-4 p-4 border border-red-300 rounded-lg bg-red-50">
                  <h3 className="text-lg font-semibold text-red-600">No Organization Found</h3>
                  <p className="text-gray-700">Please create an Organization to manage users.</p>
                </div>
              ) : (
                <div className="p-3 my-3 border border-gray-200 rounded-md ">
                  <h2 className="text-lg font-medium text-gray-700">All Users</h2>
                  <table className="border-collapse w-full">
                    <thead>
                      <tr className="w-full">
                        <th className="px-2 py-1">
                          <div className="centered justify-center text-sm font-medium text-gray-400">#</div>
                        </th>
                        <th className="px-2 py-1">
                          <div className="centered justify-start text-sm font-medium text-gray-400">Name</div>
                        </th>
                        <th className="px-2 py-1">
                          <div className="centered justify-start text-sm font-medium text-gray-400">Email</div>
                        </th>
                        <th className="px-2 py-1">
                          <div className="centered justify-start text-sm font-medium text-gray-400">Role</div>
                        </th>
                        <th className="px-2 py-1">
                          <div className="centered justify-start text-sm font-medium text-gray-400">Status</div>
                        </th>
                        <th className="px-2 py-1">
                          <div className="centered justify-start text-sm font-medium text-gray-400">Action</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(allUsers) &&
                        allUsers.map((item, index) => (
                          <tr key={item._id || index} className="border border-gray-200 border-l-0 border-r-0">
                            <td className="px-2 py-1 border border-gray-200 border-l-0 text-center">{index + 1}</td>
                            <td className="px-2 py-1 border text-base font-medium text-blue-900">{item?.full_name}</td>
                            <td className="px-2 py-1 border border-gray-200 text-xs font-medium text-gray-600">
                              {item?.email}
                            </td>
                            <td className="px-2 py-1 border border-gray-200 text-xs font-medium text-gray-600">
                              {item?.role}
                            </td>
                            <td className="px-2 py-1 border border-gray-200 text-xs font-medium text-gray-600">
                              {item?.email_verified ? 'Verified' : 'Not Verified'}
                            </td>
                            <td className="px-2 py-1 border border-r-0 border-gray-200">
                              <div className="centered justify-evenly">
                                {item?._id !== user?._id && (
                                  <FontAwesomeIcon
                                    className="m-2 cursor-pointer"
                                    icon={faDeleteLeft}
                                    onClick={() => onUserDelete(item)}
                                  />
                                )}
                                {/* <FontAwesomeIcon
                                        className="m-2 cursor-pointer"
                                        icon={faEdit}
                                      onClick={() => handleUserModalOpen(item)}
                                      /> */}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        <Modal
          title={userForm?.name ? `Edit ${userForm?.name}` : 'Add New User'}
          open={userModalOpen}
          onCancel={() => handleUserModalOpen({})}
          footer={[
            <Button className="p-4 font-medium" key="save" onClick={onUserSave}>
              Save
            </Button>,
          ]}
        >
          {userForm && (
            <div className="mt-4 space-y-3">
              <InputField
                id="user-full-name"
                type="text"
                key={userForm?.full_name || 'full_name'}
                name={'full_name'}
                placeholder="Enter full name"
                defaultValue={userForm?.full_name}
                onBlur={onInputChange}
                containerClassName="flex flex-col"
                inputClassName="p-2 border rounded bg-transparent"
                error={formErrors.full_name}
                errorClassName="text-xs"
              />

              <InputField
                id="user-email"
                type="email"
                key={userForm?.email || 'email'}
                name={'email'}
                placeholder="Enter email"
                defaultValue={userForm?.email}
                onBlur={onInputChange}
                containerClassName="flex flex-col"
                inputClassName="p-2 border rounded bg-transparent"
                error={formErrors.email}
                errorClassName="text-xs"
              />

              <PlatformRolesDropdown selectedValue={userForm?.role || ''} onPlatformRolesChange={onInputChange} />
              {formErrors.role && <p className="text-red-600">{formErrors.role}</p>}
            </div>
          )}
          {formErrors.form_error && (
            <div className="mt-4 p-4 border border-red-300 rounded-lg bg-red-50">
              <p className="text-red-600">{formErrors.form_error}</p>
            </div>
          )}
        </Modal>

        <Modal
          title={organisationDetails?.name ? `Edit ${organisationDetails?.name}` : 'Add Organization'}
          open={organisationModalOpen}
          onCancel={() => handleOrganisationModalOpen({})}
          footer={[
            <Button className="p-4 font-medium" key="save" onClick={onOrganisationSave}>
              Save
            </Button>,
          ]}
        >
          {organisationDetails && (
            <div className="mt-4 space-y-3">
              <InputField
                id="organisation-name"
                type="text"
                key={organisationDetails?.name || 'name'}
                name={'name'}
                placeholder="Enter name"
                defaultValue={organisationDetails?.name}
                onBlur={onOrganisationInputChange}
                containerClassName="flex flex-col"
                inputClassName="p-2 border rounded bg-transparent"
                error={formErrors.name}
                errorClassName="text-xs"
              />
              <InputField
                id="organisation-url"
                type="text"
                key={organisationDetails?.url || 'url'}
                name={'url'}
                placeholder="Enter url"
                defaultValue={organisationDetails?.url}
                onBlur={onOrganisationInputChange}
                containerClassName="flex flex-col"
                inputClassName="p-2 border rounded bg-transparent"
                error={formErrors.url}
                errorClassName="text-xs"
              />
            </div>
          )}
          {formErrors.form_error && (
            <div className="mt-4 p-4 border border-red-300 rounded-lg bg-red-50">
              <p className="text-red-600">{formErrors.form_error}</p>
            </div>
          )}
        </Modal>

        <Modal
          title={`Delete user ${deleteUser?.full_name}?`}
          open={deleteModalOpen}
          onOk={onDeleteUserConfirm}
          onCancel={() => onUserDelete(null)}
          okText="Confirm"
          cancelText="Cancel"
          centered
        />
      </div>
    </MainLayout>
  );
}

export default SavedUsers;
